import React, { createContext, useContext, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';

// Action types
const ActionTypes = {
  SET_LANGUAGE: 'SET_LANGUAGE',
  TOGGLE_ROI_CALCULATOR: 'TOGGLE_ROI_CALCULATOR',
  TOGGLE_AUTH_MODAL: 'TOGGLE_AUTH_MODAL',
  SET_AUTH_MODE: 'SET_AUTH_MODE',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Initial state
const initialState = {
  // Language settings
  language: 'es',

  // Modal states
  showROICalculator: false,
  showAuthModal: false,
  authMode: 'signin', // 'signin' | 'signup'

  // UI states
  isLoading: false,
  error: null,

  // User state (for future use)
  user: null,
  isAuthenticated: false,
};

// Reducer function
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LANGUAGE:
      return {
        ...state,
        language: action.payload,
      };

    case ActionTypes.TOGGLE_ROI_CALCULATOR:
      return {
        ...state,
        showROICalculator: action.payload,
      };

    case ActionTypes.TOGGLE_AUTH_MODAL:
      return {
        ...state,
        showAuthModal: action.payload,
      };

    case ActionTypes.SET_AUTH_MODE:
      return {
        ...state,
        authMode: action.payload,
      };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case ActionTypes.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      console.warn(`Unhandled action type: ${action.type}`);
      return state;
  }
};

// Create context
const AppContext = createContext();

// Context provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Action creators
  const actions = {
    setLanguage: useCallback((language) => {
      dispatch({ type: ActionTypes.SET_LANGUAGE, payload: language });
    }, []),

    toggleROICalculator: useCallback((show) => {
      dispatch({ type: ActionTypes.TOGGLE_ROI_CALCULATOR, payload: show });
    }, []),

    showSignIn: useCallback(() => {
      // Redirect directly to specified URL instead of showing modal
      window.open('https://dashboard.kumia.app/login', '_blank');
    }, []),

    showSignUp: useCallback(() => {
      // Redirect directly to specified URL instead of showing modal
      window.open('https://dashboard.kumia.app/register', '_blank');
    }, []),

    hideAuthModal: useCallback(() => {
      dispatch({ type: ActionTypes.TOGGLE_AUTH_MODAL, payload: false });
    }, []),

    setLoading: useCallback((isLoading) => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });
    }, []),

    setError: useCallback((error) => {
      dispatch({ type: ActionTypes.SET_ERROR, payload: error });
    }, []),

    clearError: useCallback(() => {
      dispatch({ type: ActionTypes.CLEAR_ERROR });
    }, []),
  };

  const value = {
    ...state,
    ...actions,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook to use the context
export const useApp = () => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }

  return context;
};

export default AppContext;